<div class="relationship-types-page page-container">
  <div class="flex flex-col gap-6">
    <div class="page-header">
      <h1 class="page-title">Modelos de Relación</h1>
      <p class="page-subtitle">
        Consulta las condiciones por defecto de cada modelo y descarga su plantilla de contrato
      </p>
    </div>

    @if (isLoading()) {
      <div class="py-12 text-center">
        <div class="flex flex-col items-center gap-3">
          <div
            class="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin"
          ></div>
          <span class="text-sm text-gray-500 dark:text-gray-400">Cargando modelos de relación...</span>
        </div>
      </div>
    } @else if (relationshipTypes.length === 0) {
      <div class="empty-box">
        <fa-icon [icon]="faHandshake" class="text-4xl text-slate-200 dark:text-slate-700 mb-3"></fa-icon>
        <h3 class="text-base font-semibold text-gray-900 dark:text-white">
          No hay modelos de relación disponibles
        </h3>
      </div>
    } @else {
      <!-- Colaborador: relación no remunerada, sin condiciones económicas -->
      @if (collaboratorType; as ct) {
        <div class="detail-card">
          <div class="card-head">
            <h3 class="card-head__title">
              <fa-icon [icon]="faFileContract" class="text-brand-red/70"></fa-icon>
              {{ ct.type }}
            </h3>
            <div class="card-head__actions">
              <button
                type="button"
                class="btn-download-template"
                [disabled]="!ct.contract_template || downloadingType === ct.type"
                [title]="ct.contract_template ? 'Descargar plantilla' : 'Sin plantilla disponible'"
                (click)="downloadTemplate(ct)"
              >
                @if (downloadingType === ct.type) {
                  <div class="spinner-sm"></div>
                } @else {
                  <fa-icon [icon]="faDownload"></fa-icon>
                }
                Plantilla
              </button>
            </div>
          </div>

          @if (relationshipTypeDescriptions[ct.type]) {
            <p class="description">{{ relationshipTypeDescriptions[ct.type] }}</p>
          }

          <div class="section-divider"></div>

          <span class="section-label">Condiciones generales</span>
          <div class="chips-row">
            <div class="chip">
              <span class="chip-label">Comisión</span>
              <span class="chip-value">No aplica</span>
            </div>
            <div class="chip">
              <span class="chip-label">Exclusividad</span>
              <span class="chip-value">No</span>
            </div>
            <div class="chip">
              <span class="chip-label">Objetivo mínimo</span>
              <span class="chip-value">No</span>
            </div>
            <div class="chip">
              <span class="chip-label">Relación comercial obligatoria</span>
              <span class="chip-value">No</span>
            </div>
            <div class="chip">
              <span class="chip-label">Seguimiento</span>
              <span class="chip-value">Según necesidad</span>
            </div>
          </div>
        </div>
      }

      <!-- Modelos sin desglose de modalidad -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        @for (rt of simpleTypes; track rt.type) {
          <div class="detail-card">
            <div class="card-head">
              <h3 class="card-head__title">
                <fa-icon [icon]="faFileContract" class="text-brand-red/70"></fa-icon>
                {{ rt.type }}
              </h3>
              <div class="card-head__actions">
                <button
                  type="button"
                  class="btn-edit-text"
                  (click)="openEditModal(rt, rt.defaults[0])"
                >
                  <fa-icon [icon]="faPen"></fa-icon>
                  Editar
                </button>
                <button
                  type="button"
                  class="btn-download-template"
                  [disabled]="!rt.contract_template || downloadingType === rt.type"
                  [title]="rt.contract_template ? 'Descargar plantilla' : 'Sin plantilla disponible'"
                  (click)="downloadTemplate(rt)"
                >
                  @if (downloadingType === rt.type) {
                    <div class="spinner-sm"></div>
                  } @else {
                    <fa-icon [icon]="faDownload"></fa-icon>
                  }
                  Plantilla
                </button>
              </div>
            </div>

            @if (relationshipTypeDescriptions[rt.type]) {
              <p class="description">{{ relationshipTypeDescriptions[rt.type] }}</p>
            }

            <div class="section-divider"></div>

            <span class="section-label">Comisiones por defecto</span>
            <div class="chips-row">
              <div class="chip">
                <span class="chip-label">Comisión fija</span>
                <span class="chip-value">{{ formatPercent(rt.defaults[0].fixed_commission) }}</span>
              </div>
              <div class="chip">
                <span class="chip-label">Comisión recurrente</span>
                <span class="chip-value">{{ formatPercent(rt.defaults[0].recurring_commission) }}</span>
              </div>
            </div>

            <span class="section-label mt-4">Condiciones generales por defecto</span>
            <div class="chips-row">
              <div class="chip">
                <span class="chip-label">Límite por operación</span>
                <span class="chip-value">{{ formatCurrency(rt.defaults[0].operation_limit) }}</span>
              </div>
              <div class="chip">
                <span class="chip-label">Periodicidad liquidación</span>
                <span class="chip-value">{{ rt.defaults[0].settlement_periodicity || '-' }}</span>
              </div>
              <div class="chip">
                <span class="chip-label">Mínimo nuevos clientes/año</span>
                <span class="chip-value">{{ formatNumber(rt.defaults[0].min_new_clients_per_year) }}</span>
              </div>
            </div>
          </div>
        }
      </div>

      <!-- Modelos con desglose por modalidad de distribución -->
      @for (rt of complexTypes; track rt.type) {
        <div class="detail-card">
          <div class="card-head">
            <h3 class="card-head__title">
              <fa-icon [icon]="faFileContract" class="text-brand-red/70"></fa-icon>
              {{ rt.type }}
            </h3>
            <button
              type="button"
              class="btn-download-template"
              [disabled]="!rt.contract_template || downloadingType === rt.type"
              [title]="rt.contract_template ? 'Descargar plantilla' : 'Sin plantilla disponible'"
              (click)="downloadTemplate(rt)"
            >
              @if (downloadingType === rt.type) {
                <div class="spinner-sm"></div>
              } @else {
                <fa-icon [icon]="faDownload"></fa-icon>
              }
              Plantilla
            </button>
          </div>

          @if (relationshipTypeDescriptions[rt.type]) {
            <p class="description">{{ relationshipTypeDescriptions[rt.type] }}</p>
          }

          <div class="section-divider"></div>

          <!-- Desktop (>=1200px): tabla comparativa -->
          <div class="table-wrapper hidden min-[1200px]:block">
            <table class="mini-table">
              <thead>
                <tr>
                  <th>Modalidad</th>
                  <th class="text-right">C. Fija</th>
                  <th class="text-right">C. Recurrente</th>
                  <th class="text-right">Límite por operación</th>
                  <th class="text-right">Periodicidad liquidación</th>
                  <th class="text-right">Mínimo clientes/año</th>
                  <th class="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                @for (def of sortedDefaults(rt); track def.distribution_modality) {
                  <tr>
                    <td class="modality-cell">
                      <span class="font-semibold">{{ def.distribution_modality }}</span>
                      @if (distributionModalityDescriptions[def.distribution_modality || '']) {
                        <span class="modality-cell__hint">{{
                          distributionModalityDescriptions[def.distribution_modality || '']
                        }}</span>
                      }
                    </td>
                    <td class="text-right">{{ formatPercent(def.fixed_commission) }}</td>
                    <td class="text-right">{{ formatPercent(def.recurring_commission) }}</td>
                    <td class="text-right">{{ formatCurrency(def.operation_limit) }}</td>
                    <td class="text-right">{{ def.settlement_periodicity || '-' }}</td>
                    <td class="text-right">{{ formatNumber(def.min_new_clients_per_year) }}</td>
                    <td class="text-right">
                      <button
                        type="button"
                        class="btn-icon-edit"
                        title="Editar condiciones"
                        (click)="openEditModal(rt, def)"
                      >
                        <fa-icon [icon]="faPen"></fa-icon>
                      </button>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>

          <!-- Móvil/tablet (<1200px): una tarjeta apilada por modalidad, mismo patrón que los modelos simples -->
          <div class="flex flex-col gap-3 min-[1200px]:hidden">
            @for (def of sortedDefaults(rt); track def.distribution_modality) {
              <div class="distribution-group">
                <div class="distribution-group__head">
                  <span class="modality-title">{{ def.distribution_modality }}</span>
                  <button
                    type="button"
                    class="btn-edit-text"
                    (click)="openEditModal(rt, def)"
                  >
                    <fa-icon [icon]="faPen"></fa-icon>
                    Editar
                  </button>
                </div>

                @if (distributionModalityDescriptions[def.distribution_modality || '']) {
                  <p class="description mb-3">
                    {{ distributionModalityDescriptions[def.distribution_modality || ''] }}
                  </p>
                }

                <span class="section-label--sub">Comisiones por defecto</span>
                <div class="chips-row">
                  <div class="chip">
                    <span class="chip-label">Comisión fija</span>
                    <span class="chip-value">{{ formatPercent(def.fixed_commission) }}</span>
                  </div>
                  <div class="chip">
                    <span class="chip-label">Comisión recurrente</span>
                    <span class="chip-value">{{ formatPercent(def.recurring_commission) }}</span>
                  </div>
                </div>

                <span class="section-label--sub mt-3">Condiciones generales por defecto</span>
                <div class="chips-row">
                  <div class="chip">
                    <span class="chip-label">Límite por operación</span>
                    <span class="chip-value">{{ formatCurrency(def.operation_limit) }}</span>
                  </div>
                  <div class="chip">
                    <span class="chip-label">Periodicidad liquidación</span>
                    <span class="chip-value">{{ def.settlement_periodicity || '-' }}</span>
                  </div>
                  <div class="chip">
                    <span class="chip-label">Mínimo nuevos clientes/año</span>
                    <span class="chip-value">{{ formatNumber(def.min_new_clients_per_year) }}</span>
                  </div>
                </div>
              </div>
            }
          </div>
        </div>
      }
    }
  </div>
</div>

@if (editingDefault) {
  <app-edit-relationship-default-modal
    [relationshipType]="editingDefault.rt.type"
    [default]="editingDefault.def"
    (close)="closeEditModal()"
    (success)="onEditSuccess($event)"
  ></app-edit-relationship-default-modal>
}
